import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// import { EnvironmentService } from '../../../services/enviroment.service';
// import { environment } from '../../../services/global-environment';;

import { environment } from '../../../services/global-environment';

interface EventRecord {
  id: string;
  date: Date;
  type: string; // 違規、罰款、稽核缺失等
  title: string;
  description: string;
  lawReference: string; // 相關法規參考
  penalty?: number; // 罰款金額（可選）
  attachments: string[]; // 附件URLs
  status: string; // 狀態：未處理、處理中、已解決等
  createdAt: Date;
}

@Component({
  selector: 'app-event-records',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './event-records.component.html',
  styleUrl: './event-records.component.scss'
})
export class EventRecordsComponent implements OnInit {
  // 表單數據
  eventData: Partial<EventRecord> = {
    date: new Date(),
    type: '',
    title: '',
    description: '',
    lawReference: '',
    status: 'new',
    attachments: []
  };

  // 所有記錄
  eventRecords: EventRecord[] = [];

  // 篩選後的記錄
  filteredRecords: EventRecord[] = [];

  // 搜尋條件
  searchTerm: string = '';
  selectedType: string = 'all';

  // 事件類型選項
  eventTypes = [
    { value: 'violation', label: '違規事件' },
    { value: 'fine', label: '罰款處分' },
    { value: 'audit', label: '稽核缺失' },
    { value: 'inspection', label: '檢查不符合項目' },
    { value: 'warning', label: '主管機關警告' }
  ];

  // 狀態選項
  statusOptions = [
    { value: 'new', label: '未處理' },
    { value: 'inProgress', label: '處理中' },
    { value: 'resolved', label: '已解決' },
    { value: 'closed', label: '已結案' }
  ];

  // 當前激活的頁籤
  activeTab: 'add' | 'list' | 'stats' = 'add';

  // 檔案上傳相關
  selectedFile: File | null = null;

  // 公司ID
  companyId = environment.companyid;

  constructor() {}

  ngOnInit(): void {
    this.loadEventRecords();
    this.applyFilters();
  }

  // 切換頁籤
  setActiveTab(tab: 'add' | 'list' | 'stats'): void {
    this.activeTab = tab;
  }

  // 從本地存儲載入記錄
  loadEventRecords(): void {
    const savedRecords = localStorage.getItem(`eventRecords_${this.companyId}`);
    if (savedRecords) {
      try {
        const records = JSON.parse(savedRecords);
        // 將日期字符串轉換回 Date 對象
        this.eventRecords = records.map((record: any) => ({
          ...record,
          date: new Date(record.date),
          createdAt: new Date(record.createdAt)
        }));
      } catch (error) {
        console.error('載入記錄失敗:', error);
        this.eventRecords = [];
      }
    }
  }

  // 儲存記錄到本地存儲
  saveEventRecords(): void {
    localStorage.setItem(`eventRecords_${this.companyId}`, JSON.stringify(this.eventRecords));
  }

  // 處理檔案選擇
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    }
  }

  // 儲存新記錄
  saveEvent(): void {
    // 基本驗證
    if (!this.eventData.title || !this.eventData.type || !this.eventData.description) {
      alert('請填寫必要欄位：標題、類型和描述');
      return;
    }

    const newRecord: EventRecord = {
      id: Date.now().toString(), // 使用時間戳當作ID
      date: this.eventData.date || new Date(),
      type: this.eventData.type || '',
      title: this.eventData.title || '',
      description: this.eventData.description || '',
      lawReference: this.eventData.lawReference || '',
      penalty: this.eventData.type === 'fine' ? this.eventData.penalty : undefined,
      attachments: [], // 實際應用中這裡會包含已上傳檔案的URL
      status: this.eventData.status || 'new',
      createdAt: new Date()
    };

    // 將新記錄添加到列表頂部
    this.eventRecords.unshift(newRecord);

    // 儲存到本地
    this.saveEventRecords();

    // 重置表單
    this.resetForm();

    // 更新過濾後的記錄
    this.applyFilters();

    // 切換到列表頁籤
    this.activeTab = 'list';

    alert('記錄已成功儲存！');
  }

  // 重置表單
  resetForm(): void {
    this.eventData = {
      date: new Date(),
      type: '',
      title: '',
      description: '',
      lawReference: '',
      status: 'new',
      attachments: []
    };
    this.selectedFile = null;
  }

  // 應用篩選條件
  applyFilters(): void {
    this.filteredRecords = this.eventRecords.filter(record => {
      // 關鍵字搜尋
      const matchesSearchTerm = !this.searchTerm ||
        record.title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        record.description.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        record.lawReference.toLowerCase().includes(this.searchTerm.toLowerCase());

      // 類型篩選
      const matchesType = this.selectedType === 'all' || record.type === this.selectedType;

      return matchesSearchTerm && matchesType;
    });
  }

  // 刪除記錄
  deleteRecord(id: string): void {
    if (confirm('確定要刪除此記錄嗎？此操作無法復原。')) {
      this.eventRecords = this.eventRecords.filter(record => record.id !== id);
      this.saveEventRecords();
      this.applyFilters();
    }
  }

  // 取得類型標籤
  getTypeLabel(typeValue: string): string {
    const type = this.eventTypes.find(t => t.value === typeValue);
    return type ? type.label : typeValue;
  }

  // 取得狀態標籤
  getStatusLabel(statusValue: string): string {
    const status = this.statusOptions.find(s => s.value === statusValue);
    return status ? status.label : statusValue;
  }

  // 計算統計數據
  getStatistics() {
    // 依類型統計
    const typeStats = this.eventTypes.map(type => {
      const count = this.eventRecords.filter(record => record.type === type.value).length;
      return { ...type, count };
    })
    .sort((a, b) => b.count - a.count); // 排序，以便最常見的類型總是在第一位

    // 依法規統計
    const lawStats: Record<string, number> = {};
    this.eventRecords.forEach(record => {
      if (record.lawReference && record.lawReference.trim()) {
        lawStats[record.lawReference] = (lawStats[record.lawReference] || 0) + 1;
      }
    });

    // 轉換為陣列並排序
    const sortedLawStats = Object.entries(lawStats)
      .map(([law, count]) => ({ law, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5); // 只取前5名

    // 計算出最常見的類型（如果有）
    const mostCommonType = typeStats.length > 0 && typeStats[0].count > 0
      ? typeStats[0]
      : null;

    return {
      typeStats,
      lawStats: sortedLawStats,
      mostCommonType
    };
  }
}
